(() => {
  const CONFIG = {
    productSection: '.maison-products-section',
    productTrack: '.maison-products-section__track',
    mediaSectionCandidates: [
      '.maison-media-products',
      '.maison-media-products-section',
      '.maison-media-products__section'
    ],
    mediaGrid: '.maison-media-products__grid'
  };

  const prepared = new WeakSet();

  function isReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  function reveal(el, delay = 0) {
    if (!el) return;
    const doReveal = () => el.classList.add('maison-motion-visible');

    if (isReducedMotion()) {
      doReveal();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setTimeout(doReveal, delay);
      return;
    }

    const rect = el.getBoundingClientRect();
    const visibleNow =
      rect.top < window.innerHeight * 0.9 &&
      rect.bottom > window.innerHeight * 0.05;

    if (visibleNow) {
      // Ensure at least one paint happens in the initial state.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setTimeout(doReveal, 100 + delay);
      }));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        setTimeout(doReveal, delay);
        obs.unobserve(entry.target);
      }
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });

    observer.observe(el);
  }

  function prepareStagger(container, itemSelector = ':scope > *') {
    if (!container || prepared.has(container)) return;
    prepared.add(container);

    let items = [];
    try {
      items = [...container.querySelectorAll(itemSelector)];
    } catch {
      items = [...container.children];
    }

    if (!items.length) return;

    container.classList.add('maison-motion-stagger');

    items.forEach((item, index) => {
      item.classList.add('maison-motion-item');
      item.style.setProperty(
        '--maison-motion-delay',
        `${Math.min(index, 8) * 80}ms`
      );
    });

    reveal(container);
  }

  function prepareProductCarousel(root) {
    if (!root || prepared.has(root)) return;
    prepared.add(root);

    root.classList.add('maison-motion-ready');
    reveal(root);

    const track = root.querySelector(CONFIG.productTrack);
    if (track) {
      prepareStagger(track, ':scope > .maison-products-section__item, :scope > *');
    }
  }

  function findMediaRoot(wrapper) {
    for (const selector of CONFIG.mediaSectionCandidates) {
      const found = wrapper.matches?.(selector) ? wrapper : wrapper.querySelector?.(selector);
      if (found) return found;
    }
    return wrapper;
  }

  function prepareMediaProducts(wrapper) {
    const root = findMediaRoot(wrapper);
    if (!root || prepared.has(root)) return;
    prepared.add(root);

    root.classList.add('maison-motion-ready');
    reveal(root);

    const grid = root.querySelector(CONFIG.mediaGrid) || wrapper.querySelector?.(CONFIG.mediaGrid);
    if (!grid) return;

    const children = [...grid.children];
    if (!children.length) return;

    // First visually substantial child is treated as editorial media.
    const mediaChild = children.find(child =>
      child.querySelector('img, video, iframe, .deferred-media, video-media')
    );

    if (mediaChild) {
      mediaChild.classList.add('maison-motion-media');
      reveal(mediaChild, 60);
    }

    // Animate remaining siblings as a restrained stagger.
    const contentChildren = children.filter(child => child !== mediaChild);
    if (contentChildren.length) {
      const proxy = document.createElement('div');
      // We do not insert the proxy; we mark the actual elements directly.
      grid.classList.add('maison-motion-stagger');
      contentChildren.forEach((child, index) => {
        child.classList.add('maison-motion-item');
        child.style.setProperty('--maison-motion-delay', `${index * 90}ms`);
      });
      reveal(grid, 80);
    }
  }

  function init(scope = document) {
    // Product carousels
    if (scope.matches?.(CONFIG.productSection)) {
      prepareProductCarousel(scope);
    }
    scope.querySelectorAll?.(CONFIG.productSection).forEach(prepareProductCarousel);

    // Media + Product sections: prefer Shopify wrapper discovery for resilience.
    const wrappers = new Set();

    if (scope.matches?.('.shopify-section[id*="maison_media_products"]')) {
      wrappers.add(scope);
    }

    scope.querySelectorAll?.('.shopify-section[id*="maison_media_products"]').forEach(el => {
      wrappers.add(el);
    });

    // Fallback: discover by grid class if section ID naming differs.
    if (scope.matches?.(CONFIG.mediaGrid)) {
      wrappers.add(scope.closest('.shopify-section') || scope.parentElement);
    }

    scope.querySelectorAll?.(CONFIG.mediaGrid).forEach(grid => {
      wrappers.add(grid.closest('.shopify-section') || grid.parentElement);
    });

    wrappers.forEach(prepareMediaProducts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));

  window.maisonMotion = {
    status() {
      return {
        productSections: [...document.querySelectorAll(CONFIG.productSection)].map(el => ({
          visible: el.classList.contains('maison-motion-visible'),
          cards: el.querySelector(CONFIG.productTrack)?.children.length || 0
        })),
        mediaSections: [...document.querySelectorAll(CONFIG.mediaGrid)].map(grid => ({
          wrapper: grid.closest('.shopify-section')?.id || '',
          children: grid.children.length,
          visible: grid.classList.contains('maison-motion-visible')
        }))
      };
    },
    replay() {
      document.querySelectorAll(
        '.maison-motion-ready, .maison-motion-stagger, .maison-motion-media'
      ).forEach(el => el.classList.remove('maison-motion-visible'));

      requestAnimationFrame(() => requestAnimationFrame(() => {
        document.querySelectorAll('.maison-motion-ready').forEach((el, i) => {
          setTimeout(() => el.classList.add('maison-motion-visible'), 80 + i * 30);
        });
        document.querySelectorAll('.maison-motion-stagger').forEach((el, i) => {
          setTimeout(() => el.classList.add('maison-motion-visible'), 120 + i * 30);
        });
        document.querySelectorAll('.maison-motion-media').forEach((el, i) => {
          setTimeout(() => el.classList.add('maison-motion-visible'), 100 + i * 30);
        });
      }));
    }
  };
})();
